import React, { useEffect, useMemo, useState } from "react";
import { Plus, Briefcase } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import AdminHeader, { notifyDealsChanged } from "@/components/admin/AdminHeader";
import DealForm from "@/components/admin/DealForm";
import DealRow from "@/components/admin/DealRow";
import LeadsInbox from "@/components/admin/LeadsInbox";
import ReviewsPanel from "@/components/admin/ReviewsPanel";
import { DEAL_STAGES, OPEN_STAGES, isDue, eur } from "@/lib/deals";

const FILTERS = [["open", "En cours"], ["due", "À relancer"], ...Object.entries(DEAL_STAGES).map(([k, v]) => [k, v.label]), ["all", "Toutes"]];

export default function AdminDeals() {
  const { authFetch } = useAuth();
  const { toast } = useToast();
  const [deals, setDeals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("deals");
  const [filter, setFilter] = useState("open");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [d, l] = await Promise.all([authFetch("/admin/deals").then((r) => r.json()), authFetch("/leads").then((r) => r.json())]);
    setDeals(Array.isArray(d) ? d : []);
    setLeads(Array.isArray(l) ? l : []);
    notifyDealsChanged();
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (payload) => {
    const id = editing?.id;
    const r = await authFetch(id ? `/admin/deals/${id}` : "/admin/deals", { method: id ? "PUT" : "POST", body: JSON.stringify(payload) });
    if (!r.ok) return false;
    setEditing(null);
    load();
    return true;
  };

  const changeStage = async (d, stage) => {
    const { id, created_at, updated_at, signed_at, ...rest } = d;
    const r = await authFetch(`/admin/deals/${id}`, { method: "PUT", body: JSON.stringify({ ...rest, stage }) });
    if (!r.ok) toast({ title: "Erreur", description: "L'étape n'a pas pu être modifiée.", variant: "destructive" });
    load();
  };

  const askReview = async (d) => {
    if (!window.confirm(`Envoyer une demande d'avis Google à ${d.client_email} ?`)) return;
    const r = await authFetch("/admin/reviews/request", { method: "POST", body: JSON.stringify({ name: d.client_name, email: d.client_email, deal_id: d.id }) });
    toast(r.ok ? { title: "Demande d'avis envoyée" } : { title: "Erreur", description: "Envoi impossible.", variant: "destructive" });
    load();
  };

  const remove = async (d) => {
    if (!window.confirm(`Supprimer l'affaire de ${d.client_name} ?`)) return;
    const r = await authFetch(`/admin/deals/${d.id}`, { method: "DELETE" });
    if (!r.ok) toast({ title: "Erreur", description: "Suppression impossible.", variant: "destructive" });
    load();
  };

  const fromLead = (l) => setEditing({
    client_name: l.full_name, client_email: l.email, client_phone: l.phone, lead_id: l.id,
    type: ["credit", "assurance", "placement"].find((n) => l.needs.includes(n)) || "credit",
    amount: Number(String(l.amount || "").replace(/[^\d.,]/g, "").replace(",", ".")) || "",
    notes: l.project_details || "",
  });

  const linked = new Set(deals.map((d) => d.lead_id).filter(Boolean));
  const inbox = leads.filter((l) => !linked.has(l.id));
  const shown = deals.filter((d) => filter === "all" || (filter === "open" ? OPEN_STAGES.includes(d.stage) : filter === "due" ? isDue(d) : d.stage === filter));

  const stats = useMemo(() => {
    const year = String(new Date().getFullYear());
    const signedYear = deals.filter((d) => d.stage === "signee" && (d.signed_at || "").startsWith(year));
    return [
      ["Affaires en cours", deals.filter((d) => OPEN_STAGES.includes(d.stage)).length, "stat-open"],
      ["À relancer", deals.filter(isDue).length, "stat-due"],
      [`Signées ${year}`, signedYear.length, "stat-signed"],
      [`Frais + commissions ${year}`, eur(signedYear.reduce((s, d) => s + (d.fees || 0) + (d.commission || 0), 0)), "stat-revenue"],
      ["Versements programmés", `${eur(deals.filter((d) => d.stage === "signee").reduce((s, d) => s + (d.scheduled_payment || 0), 0))}/mois`, "stat-scheduled"],
    ];
  }, [deals]);

  return (
    <div className="min-h-screen bg-md-bg" data-testid="admin-deals-page">
      <AdminHeader />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-4xl text-md-primary font-medium">Vos affaires</h1>
            <p className="text-md-muted mt-2">Suivez chaque dossier, de la demande à la signature, et ne ratez aucune relance.</p>
          </div>
          <button data-testid="deal-new-btn" onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-md-secondary hover:bg-md-secondaryHover text-white px-6 py-3 rounded-full font-semibold shadow-lg shadow-md-secondary/30 self-start transition-transform hover:-translate-y-0.5">
            <Plus className="w-4 h-4" /> Nouvelle affaire
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-10">
          {stats.map(([k, v, id]) => (
            <div key={id} data-testid={id} className="bg-white border border-md-border rounded-2xl p-5">
              <div className="font-serif text-2xl text-md-primary">{v}</div>
              <div className="text-[10px] uppercase tracking-widest text-md-muted mt-1">{k}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-6 border-b border-md-border mb-6">
          {[["deals", "Affaires", deals.length], ["leads", "Demandes du site à traiter", inbox.length], ["reviews", "Avis Google", null]].map(([k, l, n]) => (
            <button key={k} data-testid={`deals-tab-${k}`} onClick={() => setTab(k)} className={`pb-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-md-secondary text-md-primary" : "border-transparent text-md-muted hover:text-md-primary"}`}>
              {l} {n !== null && <span className="opacity-60">({n})</span>}
            </button>
          ))}
        </div>

        {tab === "reviews" ? <ReviewsPanel /> : tab === "leads" ? <LeadsInbox leads={inbox} onCreate={fromLead} /> : (
          <>
            <div className="flex gap-2 mb-6 flex-wrap">
              {FILTERS.map(([k, l]) => (
                <button key={k} data-testid={`deal-filter-${k}`} onClick={() => setFilter(k)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === k ? "bg-md-primary text-white" : "bg-white text-md-muted border border-md-border hover:text-md-primary"}`}>{l}</button>
              ))}
            </div>
            {shown.length === 0 ? (
              <div data-testid="deals-empty" className="text-center py-16 bg-white rounded-3xl border border-md-border text-md-muted">
                <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-40" /> Aucune affaire dans cette vue.
              </div>
            ) : (
              <div className="space-y-3" data-testid="deals-list">
                {shown.map((d) => <DealRow key={d.id} deal={d} onEdit={setEditing} onDelete={remove} onStage={changeStage} onReview={askReview} />)}
              </div>
            )}
          </>
        )}
      </main>
      <DealForm open={editing !== null} initial={editing} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}
